import { useGlobalModal } from '@/hooks/useGlobalModal';
import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import GlobalModal from '@components/GlobalModal';
import {
  CreateModalStep,
  getStepAttributes,
  stepList,
} from '@features/reports2/utils/createModalStep';
import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, Skeleton, Typography } from '@mui/material';
import { Suspense, useCallback, useEffect, useMemo, type FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { Stepper } from './Stepper';
import { useCreateForm } from './useCreateForm';
import { DevicesAndMetricsStep } from './steps/step1/1-DevicesAndMetrics';
import { TimePeriodStep } from './steps/step2/2-TimePeriod';
import { FormatAndRecipientsStep } from './steps/step3/3-FormatAndRecipients';
import { SummaryStep } from './steps/step5/5-Summary';
import { CreateReportsDefaultData } from '@features/reports2/types';
import { Access } from '@features/authorization';

interface Props {
  templateId: number | undefined;
  onClose: (skipCloseConfirmation?: boolean) => void;
  step: CreateModalStep;
  onChangeStep: (step: CreateModalStep) => void;
  onPrevStep: () => void;
  onNextStep: () => void;
  stepIndex: number;
  setIsDirty: (isDirty: boolean) => void;
  defaultData?: CreateReportsDefaultData;
}

const SuspenseComponent: FC<Props> = ({
  templateId,
  onClose,
  step,
  onPrevStep,
  onNextStep,
  stepIndex,
  setIsDirty,
  defaultData,
}) => {
  const { i18n } = useLingui();

  const { methods, onSubmit } = useCreateForm({
    templateId,
    defaultData,
    onClose,
  });

  const isDirty = methods.formState.isDirty;

  useEffect(() => {
    setIsDirty(isDirty);
  }, [setIsDirty, isDirty]);

  const StepComponent = componentByStep[step];

  const isEditing = templateId != null;

  return (
    <form onSubmit={onSubmit} onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}>
      <FormProvider {...methods}>
        <Box p={2}>
          <Box display="flex" pb={1} gap={1} alignItems="center">
            <Typography variant="h5" pl={2}>
              {isEditing ? <Trans>Modificar reporte</Trans> : <Trans>Generar reporte</Trans>}
            </Typography>
            {' - '}
            <Typography variant="h5" fontWeight="normal"><Trans>Paso Nº{stepIndex + 1}</Trans></Typography>
          </Box>
          <Box border={1} borderRadius={2}>
            <Stepper
              value={step}
              steps={stepList.map((step) => {
                const attributes = getStepAttributes(step, i18n);
                return {
                  value: step,
                  label: attributes.title,
                };
              })}
            />
            <StepComponent
              actions={{
                onClose,
                onNextStep,
                onPrevStep,
                nextStepEnabled: stepIndex < stepList.length - 1,
                prevStepEnabled: stepIndex > 0,
                onFinish: onSubmit,
                showOnFinish: stepIndex === stepList.length - 1,
                onFinishEnabled: stepIndex === stepList.length - 1,
                isEditing,
                submitLoading: methods.formState.isSubmitting,
              }}
            />
          </Box>
        </Box>
      </FormProvider>
    </form>
  );
};

export const CreateReportModal: FC = () => {
  const { globalModalProps, state, setModalState, onClose, setIsDirty } = useGlobalModal(
    'createReports',
    { closeConfirmation: true },
  );

  const [step, stepIndex] = useMemo(() => {
    let step = CreateModalStep.DevicesAndMetrics;
    if (state.open && state.step != null) step = state.step;
    const stepIndex = stepList.findIndex((s) => s === step);
    return [step, stepIndex];
  }, [state]);

  const templateId = state.open ? state.templateId : undefined;

  const onPrevStep = useCallback(() => {
    if (stepIndex > 0) {
      setModalState({
        open: true,
        step: stepIndex - 1,
        templateId,
      });
    }
  }, [setModalState, stepIndex, templateId]);

  const onNextStep = useCallback(() => {
    if (stepIndex < stepList.length - 1) {
      setModalState({
        open: true,
        step: stepIndex + 1,
        templateId,
      });
    }
  }, [setModalState, stepIndex, templateId]);

  const onChangeStep = useCallback(
    (step: CreateModalStep) => setModalState({ open: true, step: step, templateId }),
    [setModalState, templateId],
  );

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{ maxWidth: 'xl' }}
      authorization={{
        // TODO: Create a proper access for reports modal
        access: Access.Allow,
      }}
    >
      {state.open && (
        <ApolloErrorBoundary>
          <Suspense fallback={<Skeleton variant="rounded" height="80dvh" width="100%" />}>
            <SuspenseComponent
              onClose={onClose}
              templateId={state.templateId}
              step={step}
              onChangeStep={onChangeStep}
              onNextStep={onNextStep}
              onPrevStep={onPrevStep}
              stepIndex={stepIndex}
              setIsDirty={setIsDirty}
              defaultData={state.defaultData}
            />
          </Suspense>
        </ApolloErrorBoundary>
      )}
    </GlobalModal>
  );
};

const componentByStep = {
  [CreateModalStep.DevicesAndMetrics]: DevicesAndMetricsStep,
  [CreateModalStep.TimePeriod]: TimePeriodStep,
  [CreateModalStep.FormatAndRecipients]: FormatAndRecipientsStep,
  [CreateModalStep.Summary]: SummaryStep,
};
